<template>
    <div class="field" :class="{'is-error': isInvalid}">
        <label v-if="label" class="field-label">{{label}}</label>
        <extend-tag
            ref="extendTag"
            :only-existing-tags="onlyExistingTags"
            @input="update"
            @search-change="handleAsyncFind($event, {offset: defaultOffset})"
            :typeahead-activation-threshold="computedIsAsync ? 0 : 1"
            :placeholder="computedPlaceHolder"
            :disabled="computedDisabled"
            :preview="preview"
            :name="name"
            :can-add="canAdd"
            :existing-tags="existingTags"
            :typeahead="typeahead"
            :loading="computedIsLoading"
            :show-next-button="computedAsyncHasNextOptions"
            :show-prev-button="computedAsyncHasPrevOptions"
            @load-prev-items-page="handleAsyncLoadPrev"
            @load-next-items-page="handleAsyncLoadNext"
            v-model="model"/>
        <span class="field-caption" v-if="helpText">{{helpText}}</span>
    </div>
</template>

<script>
    /**
     * CSS injected in \src\vendor\index.js
     */
    import shortid from 'shortid';
    import ExtendTag from './extend-tag';
    import AsyncInputMixin from '@/components/form/async-input-mixin';
    import _ from '@/common/helpers';

    export default {
        name: 'v-tag-input',
        components: {ExtendTag},
        mixins: [new AsyncInputMixin({asyncValuePropsKey: 'model', limit: 10}).getMixin()],
        props: {
            loading: {
                type: Boolean,
                default: false,
            },
            canAdd: {
                type: Boolean,
                default: false,
            },
            label: {
                type: String,
                default: null,
            },
            value: {
                type: [String, Array],
                default: null,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            /**
             * Delete field borders and padding
             * only for preview badges list
             */
            preview: {
                type: Boolean,
                default: false,
            },
            validate: {
                type: Object,
                default: null,
            },
            placeholder: {
                type: String,
                default: '',
            },
            helpText: {
                type: String,
                default: null,
            },
            stack: {
                type: String,
                default: null,
            },
            name: {
                type: String,
                default: () => shortid.generate(),
            },
            tags: {
                type: Object,
                default: () => ({}),
            },
            onlyExistingTags: {
                type: Boolean,
                default: false,
            },
        },
        watch: {
            value(value) {
                // @voerro/vue-tagsinput package bug
                // fake events on init
                if (!_.isEqual(this.model, value)) {
                    this.prevModel = _.deepClone(value);
                    this.model = value;

                    // update value labels if async
                    if (this.computedIsAsync) {
                        this.$nextTick(() => {
                            this.async.findByValue(this.computedValueProps);
                        });
                    }
                }
            },
        },
        data() {
            return {
                model: this.value,
                prevModel: _.deepClone(this.value),
                ignoreRedraw: false, // problem with load new data on the background
                defaultOffset: 0, // start each search without offset
            };
        },
        created() {
            this.$on('async-loaded', this.redrawAsyncResults);
        },
        computed: {
            isInvalid() {
                if (this.validate) {
                    return this.validate.$invalid && this.validate.$dirty;
                }
                return false;
            },
            selectedTags() {
                // returns tags labels for selected tags
                const values = this.model && !Array.isArray(this.model)
                    ? [this.model]
                    : this.model;

                const cache = this.computedIsAsync
                    ? Object.values(this.async.getAllCacheItems()).reduce((memo, data) => ({...memo, ...data.options || {}}), {})
                    : this.tags;

                if (!values) {
                    return {};
                }
                return values.reduce((memo, value) => ({
                    ...memo,
                    [value]: cache[value],
                }), {});
            },
            cachedTags() {
                // returns tags cache
                if (!this.computedIsAsync) {
                    return this.tags;
                }
                return this.computedAsyncLastOptions || {};
            },
            existingTags() {
                // returns tags cache
                if (!this.computedIsAsync) {
                    return this.tags;
                }

                const values = this.model && !Array.isArray(this.model)
                    ? [this.model]
                    : this.model || [];

                const tags = {
                    ...this.selectedTags,
                    ...this.cachedTags,
                };
                // extend id like values while async data is loading
                values.forEach((value) => {
                    if (!tags[value]) {
                        tags[value] = _.truncate(value, {limit: 8, dots: true});
                    }
                });
                return tags;
            },
            typeahead() {
                return !!Object.keys(this.existingTags).length;
            },
            computedDisabled() {
                return this.preview || this.disabled;
            },
            computedPlaceHolder() {
                if (this.computedDisabled) {
                    return '';
                }
                if (this.computedIsLoading) {
                    return 'Looking for matching results...';
                }
                if (!this.placeholder && (this.onlyExistingTags || this.computedIsAsync)) {
                    // TODO Is this placeholder correct?
                    return 'Enter a value to find the tags';
                }
                return this.placeholder || 'Enter a value and press enter';
            },
        },
        methods: {
            update() {
                // Bug with fake events in extend-tag watch tags (init event)
                const isArray = Array.isArray(this.model) || Array.isArray(this.prevModel);
                const models = this.model && !isArray ? [this.model] : this.model || [];
                const prevModels = this.prevModel && !isArray ? [this.prevModel] : this.prevModel || [];
                const value = this.value && !isArray ? [this.value] : this.value || [];

                // @voerro/vue-tagsinput package bug
                // fake events on init
                if (_.isEqual(models, prevModels)) {
                    return undefined;
                }

                const deleted = value.filter(val => !models.includes(val));
                const created = !this.canAdd ? [] : models.filter(name => !deleted.includes(name) && !this.selectedTags[name]);
                /**
                 * TODO: i think, because of some bugs and problems with vue-tagsinput
                 * we should to use other plugin in the future
                 *
                 * deleted - tags which was deleted from value
                 * created - tags which was created as new tags in the value
                 */
                this.prevModel = _.deepClone(this.model);

                this.$emit('input', this.model);
                this.$emit('update', {
                    value: this.model,
                    created,
                });

                // we should to load more values
                // if no-close on select when values list is empty
                // if close on select after each add
                if (this.computedIsAsync) {
                    const updateTags = async () => {
                        // auto load more tags
                        const cachedLength = Object.values(this.cachedTags).length;
                        const modelsLength = models.length;
                        const prevModelsLength = prevModels.length;

                        if (cachedLength === modelsLength) {
                            // load next page
                            this.handleAsyncLoadNext();
                        } else if (prevModelsLength < modelsLength) {
                            // load nex 1 item
                            this.$refs.extendTag.inputFocus();
                            this.redrawAsyncResults();
                            this.handleAsyncLoadMore(modelsLength - prevModelsLength);
                        } else if (prevModelsLength > modelsLength) {
                            // hide popup
                            this.$refs.extendTag.hideSearchResults();
                            // load new tags
                            this.ignoreRedraw = true;
                            await this.handleAsyncLoadByQuery({
                                limit: this.async.getConfig().limit,
                                offset: this.async.getOffset(),
                            });
                        }
                    };
                    // reload tags list
                    updateTags();
                }
                return undefined;
            },
            redrawAsyncResults() {
                // reset model value (recreate selected labels)
                // npm package bug with selected tags list
                this.model = this.model && Array.isArray(this.model) ? [...this.model] : this.model;

                this.$nextTick(() => {
                    this.$refs.extendTag.tagsFromValue();
                });

                // recreate search list on async data loaded
                // npm package bug with open/close/render search results
                this.$refs.extendTag.ignoreSearchResults();

                this.$nextTick(() => {
                    if (!this.ignoreRedraw) {
                        this.$refs.extendTag.searchTag();
                    }
                    this.ignoreRedraw = false;
                });
            },
        },
    };
</script>
